import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
} from '@mui/material';
import moment from 'moment';
import { ENewsletterType, INewsletter } from '../../models/newsletter.model';

interface NewsletterDetailModalProps {
  open: boolean;
  onClose: () => void;
  newsletter: INewsletter | null;
}

const NewsletterDetailModal: React.FC<NewsletterDetailModalProps> = ({
  open,
  onClose,
  newsletter,
}) => {
  if (!newsletter) return null;

  const getNewsletterTypeLabel = (type: ENewsletterType) => {
    switch (type) {
      case ENewsletterType.Investor:
        return 'Investor';
      case ENewsletterType.Startup:
        return 'Start-up';
      case ENewsletterType.Researcher:
        return 'Researcher';
      default:
        return type;
    }
  };

  const getNewsletterTypeColor = (type: ENewsletterType): "primary" | "secondary" | "success" => {
    switch (type) {
      case ENewsletterType.Investor:
        return 'primary';
      case ENewsletterType.Startup:
        return 'secondary';
      case ENewsletterType.Researcher:
        return 'success';
      default:
        return 'primary';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          Newsletter Detail
          <Chip
            label={getNewsletterTypeLabel(newsletter.newsletter_type)}
            color={getNewsletterTypeColor(newsletter.newsletter_type)}
            size="small"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Newsletter Metadata */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Sent Date
              </Typography>
              <Typography variant="body1">
                {moment(newsletter.sent_at).format('MMMM DD, YYYY [at] HH:mm')}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Recipients
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {newsletter.recipient_count} users
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Subject Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              Subject
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                English
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {newsletter.subject_en}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Vietnamese
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {newsletter.subject_vi}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Content Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              Content
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                English Content
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  maxHeight: 300,
                  overflowY: 'auto',
                  bgcolor: 'background.paper',
                }}
                dangerouslySetInnerHTML={{ __html: newsletter.content_en }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Vietnamese Content
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  maxHeight: 300,
                  overflowY: 'auto',
                  bgcolor: 'background.paper',
                }}
                dangerouslySetInnerHTML={{ __html: newsletter.content_vi }}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewsletterDetailModal;